<template>
  <v-app>
    <v-container fluid class="my-order-screen pa-0">
      <div class="my-order-header">
        <v-btn class="back-btn" rounded depressed @click="goBack()">
          <v-icon left color="#00378e" size="45">mdi-chevron-left</v-icon>
          Back
        </v-btn>
        <div class="my-order-title">My Order</div>
      </div>

      <div class="order-list-panel">
        <div v-if="cartItems.length === 0" class="empty-order">
          No items in cart
        </div>

        <div v-for="item in cartItems" :key="item.key" class="order-row">
          <img class="order-thumb" :src="getImage(item.image)" :alt="item.name" />

          <div class="order-row-main">
            <div class="order-row-name">{{ item.name }}</div>
            <div class="order-row-option">{{ item.temperature }}</div>
            <div v-if="item.sweetness" class="order-row-option">
              {{ formatSweetness(item.sweetness) }}
            </div>
            <div class="order-edit-option">
              <v-icon size="20" color="#666">mdi-pencil</v-icon>
              <span>แก้ไขตัวเลือก</span>
            </div>
          </div>

          <button class="remove-btn" type="button" @click="removeItem(item)">
            <img src="@/assets/icons/trash.png" alt="Delete" />
          </button>

          <div class="order-row-qty">
            <v-btn icon small class="qty-btn minus" @click="decrease(item)">
              <v-icon size="18">mdi-minus-circle-outline</v-icon>
            </v-btn>
            <span>{{ item.quantity }}</span>
            <v-btn icon small class="qty-btn plus" @click="increase(item)">
              <v-icon size="18">mdi-plus-circle-outline</v-icon>
            </v-btn>
          </div>
        </div>
      </div>

      <button
        class="submit-order-btn"
        type="button"
        :disabled="loadingSubmit || cartItems.length === 0"
        @click="submitOrder()"
      >
        {{ loadingSubmit ? "Ordering..." : "Order" }}
      </button>

      <v-dialog
        v-model="OrderConfirmedDialog"
        max-width="768"
        overlay-color="#d6e8fb"
        overlay-opacity="0.75"
      >
        <v-card class="order-confirm-card" @click="closeOrderConfirmedDialog">
          <div class="confirm-icon-ring">
            <div class="confirm-icon">
              <v-icon color="white" size="64">mdi-check</v-icon>
            </div>
          </div>

          <div class="confirm-title">Order Confirmed</div>

          <div class="confirm-detail-box">
            <div class="confirm-row">
              <span>Order Room</span>
              <span>:</span>
              <span>{{ orderResult.roomName }}</span>
            </div>
            <div class="confirm-row">
              <span>Status</span>
              <span>:</span>
              <span>{{ orderResult.status }}</span>
            </div>
            <div class="confirm-row">
              <span>Estimated time</span>
              <span>:</span>
              <span>{{ orderResult.estimatedTime }}</span>
            </div>
          </div>
        </v-card>
      </v-dialog>
    </v-container>
  </v-app>
</template>

<script>
import axios from "axios";
import enurl from "@/api/environment";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { clearOrderCart, loadOrderCart, saveOrderCart } from "@/utils/orderCart";

const images = {
  americano: require("@/assets/order/americano.png"),
  espresso: require("@/assets/order/espresso.png"),
  water: require("@/assets/order/water.png"),
};

const INACTIVITY_TIMEOUT_MS = 60 * 1000;
const INACTIVITY_EVENTS = ["touchstart", "mousedown", "keydown", "wheel", "scroll"];

export default {
  name: "MyOrder",
  data() {
    return {
      url: enurl.apiUrl,
      loadingSubmit: false,
      cartItems: [],
      OrderConfirmedDialog: false,
      orderConfirmTimer: null,
      inactivityTimer: null,
      orderResult: {
        roomName: "",
        status: "Preparing",
        estimatedTime: "15-20 mins",
      },
    };
  },

  mounted() {
    INACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, this.resetInactivityTimer, true);
    });
    this.loadCartItems();
    this.resetInactivityTimer();
  },

  beforeDestroy() {
    INACTIVITY_EVENTS.forEach((eventName) => {
      window.removeEventListener(eventName, this.resetInactivityTimer, true);
    });
    clearTimeout(this.inactivityTimer);
    clearTimeout(this.orderConfirmTimer);
  },

  watch: {
    loadingSubmit() {
      this.resetInactivityTimer();
    },

    OrderConfirmedDialog() {
      this.resetInactivityTimer();
    },

    "$route.query.eventID"() {
      this.loadCartItems();
    },
  },

  methods: {
    resetInactivityTimer() {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
      if (this.loadingSubmit || this.OrderConfirmedDialog) return;
      this.inactivityTimer = setTimeout(() => {
        if (this.loadingSubmit || this.OrderConfirmedDialog) {
          this.resetInactivityTimer();
          return;
        }
        this.$router.replace("/MeetingRoomScreen").catch(() => {});
      }, INACTIVITY_TIMEOUT_MS);
    },

    loadCartItems() {
      this.cartItems = loadOrderCart(this.$route.query.eventID || "");
    },

    goBack() {
      this.$router.push({
        path: "/Order",
        query: {
          eventID: this.$route.query.eventID || "",
          roomName: this.$route.query.roomName || "",
        },
      });
    },

    getImage(imageKey) {
      if (!imageKey) return images.water;
      const source = String(imageKey).trim();
      if (/^(https?:)?\/\//.test(source) || source.startsWith("data:")) {
        return source;
      }

      if (this.isBase64Image(source)) {
        return `data:image/png;base64,${source.replace(/\s/g, "")}`;
      }

      const fileName = source.split("/").pop();
      const key = fileName.replace(/\.[^/.]+$/, "").toLowerCase();
      return images[key] || source || images.water;
    },

    isBase64Image(value) {
      const source = value.replace(/\s/g, "");
      return source.length > 100 && /^[A-Za-z0-9+/]+={0,2}$/.test(source);
    },

    formatSweetness(sweetness) {
      if (!sweetness) return "";
      return String(sweetness).includes("%") ? sweetness : `${sweetness} %`;
    },

    persist() {
      saveOrderCart(this.cartItems, this.$route.query.eventID || "");
    },

    decrease(item) {
      if (item.quantity > 1) {
        item.quantity -= 1;
      } else {
        this.removeItem(item);
        return;
      }
      this.persist();
    },

    increase(item) {
      item.quantity += 1;
      this.persist();
    },

    removeItem(item) {
      this.cartItems = this.cartItems.filter((cartItem) => cartItem.key !== item.key);
      this.persist();
    },

    getResponseField(source, fields) {
      return fields.reduce((value, field) => {
        return value !== undefined && value !== null && value !== ""
          ? value
          : source?.[field];
      }, undefined);
    },

    showOrderConfirmedDialog(data = {}) {
      this.orderResult = {
        roomName:
          this.getResponseField(data, [
            "roomName",
            "RoomName",
            "orderRoom",
            "OrderRoom",
          ]) ||
          this.$route.query.roomName ||
          "Meeting Room 01",
        status:
          this.getResponseField(data, ["statusName", "StatusName", "status", "Status"]) ||
          "Preparing",
        estimatedTime:
          this.getResponseField(data, [
            "estimatedTime",
            "EstimatedTime",
            "estimateTime",
            "EstimateTime",
          ]) || "15-20 mins",
      };
      this.OrderConfirmedDialog = true;
      clearTimeout(this.orderConfirmTimer);
      this.orderConfirmTimer = setTimeout(() => {
        this.closeOrderConfirmedDialog();
      }, 3000);
    },

    closeOrderConfirmedDialog() {
      if (!this.OrderConfirmedDialog) return;

      this.OrderConfirmedDialog = false;
      clearTimeout(this.orderConfirmTimer);
      this.$router.push({
        path: "/Order",
        query: {
          eventID: this.$route.query.eventID || "",
          roomName: this.$route.query.roomName || "",
        },
      });
    },

    async submitOrder() {
      if (this.loadingSubmit || this.cartItems.length === 0) return;

      const eventID = this.$route.query.eventID || "";
      if (!eventID) {
        Swal.fire({
          icon: "warning",
          width: 900,
          text: "ไม่พบ EventID ของห้องประชุม กรุณากลับไปหน้า Meeting Room แล้วกด Order ใหม่",
        });
        return;
      }

      const payload = {
        eventID,
        items: this.cartItems.map((item) => ({
          beverageID: Number(item.id),
          temperatureID: Number(item.temperatureID || 0),
          sweetnessID: item.sweetnessID ? Number(item.sweetnessID) : null,
          amount: Number(item.quantity || 0),
        })),
      };

      this.loadingSubmit = true;
      try {
        const res = await axios.post(
          `${this.url}Transaction/addTransactionOrder`,
          payload
        );

        if (res.data.status === 0) {
          this.cartItems = [];
          clearOrderCart();
          this.showOrderConfirmedDialog(res.data.data || {});
          return;
        }

        Swal.fire({
          icon: "warning",
          width: 900,
          text: res.data.message || "Order failed",
        });
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Cannot connect to server";

        Swal.fire({
          icon: "warning",
          width: 900,
          text: message,
        });
      } finally {
        this.loadingSubmit = false;
      }
    },
  },
};
</script>

<style scoped>
.my-order-screen {
  min-height: 100vh;
  background: #eaf4ff url("~@/assets/backgroundroom.png");
  background-size: cover;
  background-position: left bottom;
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 48px 35px 0 !important;
}

.my-order-header {
  width: 100%;
  height: 83px;
  border-radius: 42px;
  background: #003f96;
  display: flex;
  align-items: center;
  position: relative;
  box-shadow: 0 6px 6px rgba(0, 0, 0, 0.25);
}

.back-btn {
  position: absolute;
  left: 27px;
  height: 56px !important;
  min-width: 124px !important;
  background: white !important;
  color: #00378e !important;
  font-size: 25px;
  text-transform: none;
  padding: 0 22px 0 12px !important;
}

.my-order-title {
  width: 100%;
  text-align: center;
  color: white;
  font-size: 40px;
  font-weight: 800;
}

.order-list-panel {
  width: min(1332px, 100%);
  height: 718px;
  margin: 20px auto 0;
  background: transparent;
  border-radius: 18px;
  padding: 0;
  box-shadow: none;
  overflow: hidden auto;
}

.empty-order {
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #003f96;
  font-size: 26px;
  font-weight: 700;
}

.order-row {
  width: 100%;
  height: 149.7994px;
  display: grid;
  grid-template-columns: 152px 1fr 170px;
  align-items: center;
  gap: 20px;
  margin-bottom: 6px;
  padding: 13px 31px 13px 12px;
  background: rgba(255, 255, 255, 0.96);
  border-bottom: 0;
  border-radius: 30px;
  position: relative;
}

.order-row:last-child {
  border-bottom: 0;
}

.order-thumb {
  width: 152px;
  height: 120px;
  border-radius: 26px;
  object-fit: cover;
}

.order-row-name {
  color: #003f96;
  font-size: 27px;
  font-weight: 800;
  line-height: 1.1;
}

.order-row-option {
  color: #003f96;
  font-size: 17px;
  line-height: 1.45;
}

.order-edit-option {
  margin-top: 10px;
  color: #666;
  font-size: 17px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.remove-btn {
  width: 35px;
  height: 35px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  position: absolute;
  top: 18px;
  right: 31px;
  padding: 0;
}

.remove-btn img {
  width: 35px;
  height: 35px;
  display: block;
  object-fit: contain;
}

.order-row-qty {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #003f96;
  font-size: 24px;
  align-self: end;
  grid-column: 3;
  margin-bottom: 8px;
}

.qty-btn.minus {
  color: red !important;
}

.qty-btn.plus {
  color: #003f96 !important;
}

.order-row-qty .qty-btn {
  width: 24px !important;
  height: 24px !important;
}

.order-row-qty .qty-btn ::v-deep .v-icon {
  font-size: 24px !important;
}

.submit-order-btn {
  position: absolute;
  z-index: 2;
  display: block;
  left: 160px;
  right: 160px;
  bottom: 42px;
  width: auto;
  max-width: none;
  height: 70px;
  margin: 0;
  border: 0;
  border-radius: 35px;
  background: #003f96;
  color: white;
  font-size: 25px;
  cursor: pointer;
  box-shadow: 0 3px 4px rgba(0, 0, 0, 0.22);
}

.submit-order-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.order-confirm-card {
  width: 768px;
  max-width: calc(100vw - 32px);
  min-height: 526px;
  border-radius: 28px !important;
  background: #ffffff !important;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 64px 56px 52px;
  box-shadow: 0 5px 0 rgba(0, 63, 150, 0.55) !important;
  color: #003f96;
  cursor: pointer;
}

.confirm-icon-ring {
  width: 104px;
  height: 104px;
  border-radius: 50%;
  background: #eaf4ff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.confirm-icon {
  width: 78px;
  height: 78px;
  border-radius: 50%;
  background: #003f96;
  display: flex;
  align-items: center;
  justify-content: center;
}

.confirm-title {
  margin-top: 31px;
  color: #003f96;
  font-size: 30px;
  font-weight: 800;
  line-height: 1.2;
  text-align: center;
}

.confirm-detail-box {
  width: 100%;
  max-width: 520px;
  min-height: 160px;
  margin-top: 42px;
  border-radius: 20px;
  background: #ecebea;
  padding: 58px 90px 48px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.confirm-row {
  display: grid;
  grid-template-columns: 156px 20px 1fr;
  gap: 18px;
  color: #003f96;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.15;
}

.confirm-row span:last-child {
  font-weight: 500;
}

.my-order-screen::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 156px;
  background: white;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
  z-index: 1;
}

@media only screen and (min-width: 761px) and (max-width: 1366px) {
  .my-order-screen {
    padding: 24px 24px 120px !important;
  }

  .my-order-header {
    height: 68px;
  }

  .back-btn {
    height: 46px !important;
    min-width: 104px !important;
    font-size: 18px;
  }

  .my-order-title {
    font-size: 32px;
  }

  .order-list-panel {
    width: 100%;
    height: calc(100vh - 240px);
    min-height: 360px;
    margin-top: 20px;
  }

  .order-row {
    height: 132px;
    grid-template-columns: 120px minmax(0, 1fr) 150px;
    padding: 12px 20px;
  }

  .order-thumb {
    width: 112px;
    height: 96px;
  }

  .order-row-name {
    font-size: 23px;
  }

  .submit-order-btn {
    left: 64px;
    right: 64px;
    bottom: 24px;
    height: 58px;
  }

  .my-order-screen::after {
    height: 108px;
  }
}

@media only screen and (max-width: 760px) {
  .my-order-screen {
    padding: 16px 16px calc(20px + env(safe-area-inset-bottom)) !important;
    overflow: auto;
  }

  .my-order-header {
    width: 100%;
    height: 62px;
  }

  .back-btn {
    width: 44px;
    height: 44px !important;
    min-width: 44px !important;
    padding: 0 !important;
    font-size: 0;
  }

  .my-order-title {
    padding: 0 48px;
    font-size: 24px;
  }

  .order-list-panel {
    width: 100%;
    position: relative;
    height: auto;
    max-height: none;
    margin: 20px 0 0;
    overflow: visible;
  }

  .order-row {
    width: 100%;
    height: auto;
    min-height: 112px;
    grid-template-columns: 72px minmax(0, 1fr);
    gap: 12px;
    margin-bottom: 10px;
    padding: 12px 44px 12px 12px;
    border-radius: 16px;
  }

  .order-thumb {
    width: 72px;
    height: 72px;
    border-radius: 14px;
  }

  .order-row-name {
    font-size: 18px;
    line-height: 1.25;
  }

  .order-row-option,
  .order-edit-option {
    font-size: 14px;
  }

  .remove-btn {
    top: 8px;
    right: 4px;
    width: 44px;
    height: 44px;
  }

  .remove-btn img {
    width: 28px;
    height: 28px;
  }

  .order-row-qty {
    grid-column: 2;
    justify-content: flex-start;
    width: auto;
    margin: 6px 0 0;
    gap: 12px;
    font-size: 18px;
  }

  .order-row-qty .qty-btn {
    width: 44px !important;
    height: 44px !important;
  }

  .submit-order-btn {
    position: relative;
    width: 100%;
    height: 54px;
    left: auto;
    right: auto;
    bottom: auto;
    margin: 20px 0 0;
    font-size: 18px;
  }

  .my-order-screen::after {
    display: none;
  }

  .back-btn ::v-deep .v-icon {
    margin: 0 !important;
  }

  .order-confirm-card {
    min-height: 420px;
    padding: 42px 22px 36px;
  }

  .confirm-title {
    font-size: 24px;
  }

  .confirm-detail-box {
    margin-top: 28px;
    padding: 34px 22px 30px;
  }

  .confirm-row {
    grid-template-columns: 116px 12px 1fr;
    gap: 10px;
    font-size: 14px;
  }
}
</style>
